// Markets: the cities that get an indexable city page (securspace-ui#3049), at
// /yards/<country>/<state>/<city-slug> (securspace-ui#3052). The sitemap lists exactly
// these; SearchSEO noindexes every other search page.
//
// Curation rule (checked against the live yards feed on 2026-09-28):
//   - at least 3 active yards within 50 miles of the market's centre, and
//   - no other market centre within 50 miles, so no two city pages list the same set
//     of yards (a few border yards can still appear on both of two neighbours).
// If a market later loses its yards, SearchSEO's runtime noindex is the safety net;
// take it out of this list at the next review.
//
// Coordinates are Google's geocode of "<city>, <state>, US", looked up once and committed.
// Plain ESM so scripts/generate-sitemap.mjs can import it; TypeScript reads it through allowJs.

/** @typedef {{ city: string, state: string, country: string, lat: number, lng: number }} Market */

/** @type {ReadonlyArray<Market>} */
export const MARKETS = Object.freeze([
  { city: 'Atlanta', state: 'GA', country: 'US', lat: 33.7501, lng: -84.3885 },
  { city: 'Baltimore', state: 'MD', country: 'US', lat: 39.2905, lng: -76.6104 },
  { city: 'Charleston', state: 'SC', country: 'US', lat: 32.7833, lng: -79.932 },
  { city: 'Charlotte', state: 'NC', country: 'US', lat: 35.2271, lng: -80.8409 },
  { city: 'Chicago', state: 'IL', country: 'US', lat: 41.8832, lng: -87.6324 },
  { city: 'Columbus', state: 'OH', country: 'US', lat: 39.9625, lng: -83.0032 },
  { city: 'Dallas', state: 'TX', country: 'US', lat: 32.7767, lng: -96.797 },
  { city: 'Detroit', state: 'MI', country: 'US', lat: 42.3297, lng: -83.0425 },
  { city: 'Greenville', state: 'SC', country: 'US', lat: 34.8485, lng: -82.4 },
  { city: 'Houston', state: 'TX', country: 'US', lat: 29.7601, lng: -95.3701 },
  { city: 'Indianapolis', state: 'IN', country: 'US', lat: 39.7691, lng: -86.158 },
  { city: 'Jacksonville', state: 'FL', country: 'US', lat: 30.3298, lng: -81.6592 },
  { city: 'Kansas City', state: 'MO', country: 'US', lat: 39.0997, lng: -94.5786 },
  { city: 'Laredo', state: 'TX', country: 'US', lat: 27.5036, lng: -99.5076 },
  { city: 'Las Vegas', state: 'NV', country: 'US', lat: 36.1716, lng: -115.1391 },
  { city: 'Los Angeles', state: 'CA', country: 'US', lat: 34.0549, lng: -118.2426 },
  { city: 'Memphis', state: 'TN', country: 'US', lat: 35.1486, lng: -90.0519 },
  { city: 'Miami', state: 'FL', country: 'US', lat: 25.7617, lng: -80.1918 },
  { city: 'Milwaukee', state: 'WI', country: 'US', lat: 43.041, lng: -87.9097 },
  { city: 'Minneapolis', state: 'MN', country: 'US', lat: 44.9778, lng: -93.265 },
  { city: 'Nashville', state: 'TN', country: 'US', lat: 36.1627, lng: -86.7816 },
  { city: 'Newark', state: 'NJ', country: 'US', lat: 40.7315, lng: -74.1745 },
  { city: 'Norfolk', state: 'VA', country: 'US', lat: 36.8508, lng: -76.2859 },
  { city: 'Oakland', state: 'CA', country: 'US', lat: 37.8044, lng: -122.2712 },
  { city: 'Philadelphia', state: 'PA', country: 'US', lat: 39.9526, lng: -75.1652 },
  { city: 'Phoenix', state: 'AZ', country: 'US', lat: 33.4483, lng: -112.0725 },
  { city: 'Portland', state: 'OR', country: 'US', lat: 45.5152, lng: -122.6784 },
  { city: 'Sacramento', state: 'CA', country: 'US', lat: 38.5781, lng: -121.4944 },
  { city: 'Salt Lake City', state: 'UT', country: 'US', lat: 40.7606, lng: -111.8881 },
  { city: 'San Antonio', state: 'TX', country: 'US', lat: 29.4252, lng: -98.4946 },
  { city: 'Savannah', state: 'GA', country: 'US', lat: 32.0809, lng: -81.0912 },
  { city: 'Seattle', state: 'WA', country: 'US', lat: 47.6061, lng: -122.3328 },
  { city: 'St. Louis', state: 'MO', country: 'US', lat: 38.6274, lng: -90.1982 },
  { city: 'Tampa', state: 'FL', country: 'US', lat: 27.9517, lng: -82.4588 },
]);

/** The curation rule's radius: a market's yards, and a yard's market, are within this. */
export const MARKET_RADIUS_MILES = 50;

/**
 * Great-circle (haversine) distance in miles.
 * @param {{ lat: number, lng: number }} a
 * @param {{ lat: number, lng: number }} b
 */
export const milesBetween = (a, b) => {
  const rad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * rad;
  const dLng = (b.lng - a.lng) * rad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) ** 2;
  return 2 * 3958.8 * Math.asin(Math.sqrt(h));
};

/**
 * The nearest market within the market radius of a point (a yard), or undefined.
 * @param {{ lat: number, lng: number }} point
 * @returns {Market | undefined}
 */
export const nearestMarket = (point) => {
  let nearest;
  let nearestMiles = MARKET_RADIUS_MILES;
  for (const m of MARKETS) {
    const miles = milesBetween(point, m);
    if (miles <= nearestMiles) {
      nearest = m;
      nearestMiles = miles;
    }
  }
  return nearest;
};

/**
 * The other markets nearest to a market, closest first ("nearby markets" on a market
 * page, securspace-ui#3056).
 * @param {Market} market
 * @param {number} [count]
 * @returns {Market[]}
 */
export const nearestMarkets = (market, count = 3) =>
  MARKETS.filter((m) => m !== market)
    .map((m) => ({ m, miles: milesBetween(market, m) }))
    .sort((a, b) => a.miles - b.miles)
    .slice(0, count)
    .map(({ m }) => m);

/**
 * The market for a city and two-letter state (and country, when given), ignoring case
 * and surrounding spaces.
 * @param {string | undefined} city
 * @param {string | undefined} state
 * @param {string} [country]
 * @returns {Market | undefined}
 */
export const findMarketByCity = (city, state, country) => {
  const c = city?.trim().toLowerCase();
  const s = state?.trim().toUpperCase();
  const co = country?.trim().toUpperCase();
  return MARKETS.find((m) => m.city.toLowerCase() === c && m.state === s && (!co || m.country === co));
};

/**
 * URL slug: lowercase, hyphens, no punctuation or accents ("St. Louis" → "st-louis").
 * Market paths are indexed, so markets.test.ts pins every one against changes here.
 * @param {string} text
 */
export const slugify = (text) =>
  text
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '')
    .replace(/--+/g, '-');

/**
 * The /yards address of a market, or of any city: /yards/us/ca/los-angeles.
 * @param {{ city: string, state: string, country?: string }} place
 */
export const marketPath = ({ city, state, country = 'US' }) =>
  `/yards/${country.toLowerCase()}/${state.toLowerCase()}/${slugify(city)}`;

/**
 * The market a /yards/<country>/<state>/<slug> address names, ignoring the case of
 * country and state.
 * @param {string} country
 * @param {string} state
 * @param {string} slug
 * @returns {Market | undefined}
 */
export const findMarket = (country, state, slug) =>
  MARKETS.find((m) => m.country.toLowerCase() === country.toLowerCase()
    && m.state.toLowerCase() === state.toLowerCase()
    && slugify(m.city) === slug);
